import React, { useMemo } from 'react';

import {
  DateField,
  RecordContextProvider,
  usePermissions,
} from 'react-admin';

import {
  Box,
  Grid,
  CardContent,
  Typography,
  Card,
  CardHeader,
  Divider,
  Alert,
} from '@mui/material';
import { Alpha3Code } from 'i18n-iso-countries';
import { isEmpty } from 'lodash';

import useEntityData from '../../../../hooks/useEntityData';

import OrgChartTree from '../../../../customFields/OrgChartTree';

import PurchaseButton from '../../../layout/button/PurchaseButton';
import actions, { Actions } from '../../../../constants/actions';
import NoResults from '../../../layout/NoResults';
import { EntityTypes } from '../../../../constants/entityTypes';
import hasAccess from '../../../../utilities/hasAccess';
import PermissionMissing from '../../../utils/PermissionMissing';

type CompanyOwnership = {
  id: string;
  createdAt: string;
  ownerNodes: {
    id: number;
    entityId: string;
    entityType: EntityTypes;
    company: boolean;
    companyId: string;
    controlDate: string;
    controlInterval: string;
    residence: Alpha3Code;
    level: number;
    name: string;
    parentId: number;
    personalNumber: string;
    quota: string;
    shareCount: number;
  }[];
}

export type BeneficialOwners = {
  id: string;
  createdAt: string;
  beneficialOwners: {
    entityId?: string;
    entityType?: EntityTypes;
    controlTypes?: {
      association?: {
        companyId?: string;
        companyName?: string;
        type?: {
          code?: string;
          description?: string;
        };
      };
      code?: string;
      description?: string;
    }[];
    extentOfControl?: {
      code?: string;
      description?: string;
      ownership?: {
        lowerBound?: number;
        upperBound?: number;
      };
      vote?: {
        lowerBound?: number;
        upperBound?: number;
      };
    };
    person?: {
      birthDate?: string;
      citizenship?: {
        code?: string;
        description?: string;
      };
      citizenships?: Alpha3Code[];
      coordinationNumber: string;
      countryResidence: {
        code: string;
        description: string;
      };
      firstNames?: string[];
      gdNumber?: string;
      givenName?: string;
      middleName?: string;
      personalNumber?: string;
      surName?: string;
      nationalIdentifier?: {
        country: Alpha3Code;
        identifier: string;
      }[];
    };
  }[];
  errorMessages?: {
    code?: string;
    description?: string;
  }[];
}

export type AltBeneficialOwners = {
  id: string;
  createdAt: string;
  altBeneficialOwners: {
    entityId?: string;
    entityType?: EntityTypes;
    altSource: string;
    boardMemberInfo: {
      description: string;
      roleCode: string;
    };
    name: string;
    residence: Alpha3Code;
    ownerInfo: {
      shareQuotas: {
        company: string;
        companyId: string;
        path: string;
        quota: string;
      }[];
      totalQuota: string;
    };
    personalNumber: string;
  }[];
}

const useOwnership = () => {
  const {
    data: ownershipData,
    handlePurchase: handlePurchaseOwnership,
    isLoading: ownershipIsLoading,
  } = useEntityData<CompanyOwnership>({
    source: 'company-ownership',
    getAction: actions.BUSINESS_GET_OWNERSHIP,
  });
  const {
    data: beneficialOwnersData,
    handlePurchase: handlePurchaseBenOwners,
    isLoading: benOwnerIsLoading,
  } = useEntityData<BeneficialOwners>({
    resource: 'businesses',
    source: 'beneficial-owners',
    getAction: actions.BUSINESS_GET_OWNERSHIP,
  });
  const {
    data: altBeneficialOwnersData,
    handlePurchase: handlePurchaseAltBenOwners,
    isLoading: altBenOwnerIsLoading,
  } = useEntityData<AltBeneficialOwners>({
    resource: 'businesses',
    source: 'alt-beneficial-owners',
    getAction: actions.BUSINESS_GET_OWNERSHIP,
  });

  return {
    ownershipData,
    beneficialOwnersData,
    altBeneficialOwnersData,
    handlePurchaseBenOwners,
    handlePurchaseOwnership,
    handlePurchaseAltBenOwners,
    isLoading: ownershipIsLoading || benOwnerIsLoading || altBenOwnerIsLoading,
  };
};

const OwnerShip = () => {
  const {
    ownershipData,
    beneficialOwnersData,
    altBeneficialOwnersData,
    handlePurchaseAltBenOwners,
    handlePurchaseBenOwners,
    handlePurchaseOwnership,
    isLoading,
  } = useOwnership();

  const beneficialOwners = beneficialOwnersData?.beneficialOwners;
  const altBeneficialOwners = altBeneficialOwnersData?.altBeneficialOwners;
  const { permissions } = usePermissions<Actions[]>();
  const hasPermission = useMemo(
    () => hasAccess(permissions, actions.BUSINESS_GET_OWNERSHIP),
    [permissions],
  );

  return (
    <Grid container spacing={6}>
      {isEmpty(beneficialOwners) && (
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <Card variant="outlined">
            <CardHeader
              title="Beneficial owner"
              subheader={(
                hasPermission && (
                  <Box display="flex" alignItems="baseline">
                    <Typography>Latest update:</Typography>
                    <DateField
                      sx={{ marginLeft: 1, fontWeight: 'bold' }}
                      source="updateDate"
                      record={{ updateDate: beneficialOwnersData?.createdAt }}
                      emptyText="-"
                      showTime
                    />
                  </Box>
                )
              )}
              action={(
                <PurchaseButton
                  loading={isLoading}
                  label="Get beneficial owners"
                  onClick={handlePurchaseBenOwners}
                  action={actions.BUSINESS_CREATE_OWNERSHIP}
                />
              )}
            />
            <Divider />
            {
              beneficialOwnersData?.errorMessages
              && (beneficialOwnersData?.errorMessages.map((error) => (
                <Grid margin={1} paddingTop={4} container justifyContent="center" key={error.code}>
                  <Alert severity="warning">{error.description}</Alert>
                </Grid>
              ))
              )
            }
            {hasPermission ? (
              <NoResults />
            ) : (
              <CardContent>
                <Box display="flex" alignItems="center" width="100%" flexDirection="column">
                  <PermissionMissing />
                </Box>
              </CardContent>
            )}
          </Card>
        </Grid>
      )}
      {beneficialOwners?.map((owner) => (
        <Grid
          key={`${owner?.entityId}`}
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <Card variant="outlined">
            <CardHeader
              title="Beneficial owner"
              subheader={(
                hasPermission && (
                  <Box display="flex" alignItems="baseline">
                    <Typography>Latest update:</Typography>
                    <DateField
                      sx={{ marginLeft: 1, fontWeight: 'bold' }}
                      source="updateDate"
                      record={{ updateDate: beneficialOwnersData?.createdAt }}
                      emptyText="-"
                      showTime
                    />
                  </Box>
                )
              )}
              action={(
                <PurchaseButton
                  loading={isLoading}
                  label="Get beneficial owners"
                  onClick={handlePurchaseBenOwners}
                  action={actions.BUSINESS_CREATE_OWNERSHIP}
                />
              )}
            />
            <Divider />
            <CardContent>
              {
                beneficialOwnersData?.errorMessages
                && (beneficialOwnersData?.errorMessages.map((error) => (
                  <Grid container justifyContent="center" key={error.code}>
                    <Alert severity="warning">{error.description}</Alert>
                  </Grid>
                ))
                )
              }
              {hasPermission ? (
                <Grid container spacing={6}>
                  <Grid size={3}>
                    <Box>
                      <Typography variant="smallTitle">First names</Typography>
                      <Typography>
                        {owner?.person?.firstNames?.join(', ')}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={3}>
                    <Box>
                      <Typography variant="smallTitle">Last name</Typography>
                      <Typography>
                        {owner?.person?.surName}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={3}>
                    <Box>
                      <Typography variant="smallTitle">National identifier</Typography>
                      <Typography>
                        {owner?.person?.personalNumber}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={3} />
                  <Grid size={8}>
                    <Typography variant="smallTitle">Citizenship</Typography>
                  </Grid>
                  <Grid size={4}>
                    <Typography variant="smallTitle">Country of residence</Typography>
                  </Grid>
                  <Grid size={1}>
                    <Box>
                      <Typography variant="smallTitle">Code</Typography>
                      <Typography>
                        {owner?.person?.citizenship?.code}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={1}>
                    <Box>
                      <Typography variant="smallTitle">Description</Typography>
                      <Typography>
                        {owner?.person?.citizenship?.description}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={6} />
                  <Grid size={1}>
                    <Box>
                      <Typography variant="smallTitle">Code</Typography>
                      <Typography>
                        {owner?.person?.countryResidence?.code}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={1}>
                    <Box>
                      <Typography variant="smallTitle">Description</Typography>
                      <Typography>
                        {owner?.person?.countryResidence?.description}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={12}>
                    <Box>
                      <Typography variant="smallTitle">Control types</Typography>
                      <ul>
                        {owner?.controlTypes?.map((type) => (
                          <li key={type?.description}>
                            {type?.description}
                          </li>
                        ))}
                      </ul>
                    </Box>
                  </Grid>
                  <Grid size={12}>
                    <Box>
                      <Typography variant="smallTitle">Extent of control</Typography>
                      <Typography>
                        {owner?.extentOfControl?.description}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              ) : (
                <Box display="flex" alignItems="center" width="100%" flexDirection="column">
                  <PermissionMissing />
                </Box>
              ) }
            </CardContent>
          </Card>
        </Grid>
      ))}
      {isEmpty(altBeneficialOwners) && (
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <Card variant="outlined">
            <CardHeader
              title="Alternative beneficial owner"
              subheader={(
                hasPermission && (
                  <Box display="flex" alignItems="baseline">
                    <Typography>Latest update:</Typography>
                    <DateField
                      sx={{ marginLeft: 1, fontWeight: 'bold' }}
                      source="updateDate"
                      record={{ updateDate: altBeneficialOwnersData?.createdAt }}
                      emptyText="-"
                      showTime
                    />
                  </Box>
                )
              )}
              action={(
                <PurchaseButton
                  loading={isLoading}
                  label="Get alternative beneficial owner"
                  onClick={handlePurchaseAltBenOwners}
                  action={actions.BUSINESS_CREATE_OWNERSHIP}
                />
              )}
            />
            <Divider />
            {hasPermission ? (
              <NoResults />
            ) : (
              <CardContent>
                <Box display="flex" alignItems="center" width="100%" flexDirection="column">
                  <PermissionMissing />
                </Box>
              </CardContent>
            )}
          </Card>
        </Grid>
      )}
      {altBeneficialOwners?.map((owner) => (
        <Grid
          key={`${owner.name}`}
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <Card variant="outlined">
            <CardHeader
              title="Alternative beneficial owner"
              subheader={(
                hasPermission ?? (
                  <Box display="flex" alignItems="baseline">
                    <Typography>Latest update:</Typography>
                    <DateField
                      sx={{ marginLeft: 1, fontWeight: 'bold' }}
                      source="updateDate"
                      record={{ updateDate: altBeneficialOwnersData?.createdAt }}
                      emptyText="-"
                      showTime
                    />
                  </Box>
                )
              )}
              action={(
                <PurchaseButton
                  loading={isLoading}
                  label="Get alternative beneficial owner"
                  onClick={handlePurchaseAltBenOwners}
                  action={actions.BUSINESS_CREATE_OWNERSHIP}
                />
              )}
            />
            <Divider />
            <CardContent>
              {hasPermission ? (
                <Grid container spacing={6}>
                  <Grid size={6}>
                    <Box>
                      <Typography variant="smallTitle">Name</Typography>
                      <Typography>
                        {owner.name}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={6}>
                    <Box>
                      <Typography variant="smallTitle">National identifier</Typography>
                      <Typography>
                        {owner.personalNumber}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={6}>
                    <Box>
                      <Typography variant="smallTitle">Description</Typography>
                      <Typography>
                        {owner.boardMemberInfo.description}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={6}>
                    <Box>
                      <Typography variant="smallTitle">Total quota</Typography>
                      <Typography>
                        {Number.parseFloat(owner.ownerInfo.totalQuota) * 100}
                        %
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              ) : (
                <Box display="flex" alignItems="center" width="100%" flexDirection="column">
                  <PermissionMissing />
                </Box>
              ) }
            </CardContent>
          </Card>
        </Grid>
      ))}
      <Grid size={12}>
        <Card variant="outlined">
          <CardHeader
            title="Ownership tree"
            subheader={(
              hasPermission && (
                <Box display="flex" alignItems="baseline">
                  <Typography>Latest update:</Typography>
                  <DateField
                    sx={{ marginLeft: 1, fontWeight: 'bold' }}
                    source="updateDate"
                    record={{ updateDate: ownershipData?.createdAt }}
                    emptyText="-"
                    showTime
                  />
                </Box>
              )
            )}
            action={(
              <PurchaseButton
                loading={isLoading}
                label="Get ownership tree"
                onClick={handlePurchaseOwnership}
                action={actions.BUSINESS_CREATE_OWNERSHIP}
              />
            )}
          />
          <Divider />
          <CardContent>
            {hasPermission ? (
              <RecordContextProvider value={ownershipData}>
                <OrgChartTree />
              </RecordContextProvider>
            ) : (
              <Box display="flex" alignItems="center" width="100%" flexDirection="column">
                <PermissionMissing />
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default OwnerShip;
